import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ROOT_URL_SUPVISION_BACK } from "../../common/utils/apiConstants";
import { isoStringToDate, isoStrToDate } from "../../common/utils/helperFunctions";

const timeInCacheShort = 0;
// eslint-disable-next-line no-unused-vars
const timeInCacheMedium = 1800;
// eslint-disable-next-line no-unused-vars
const timeInCacheLong = 3600;

const baseQuery = fetchBaseQuery({
  baseUrl: ROOT_URL_SUPVISION_BACK,
  prepareHeaders: (headers, { getState }) => {
    const authToken = getState().auth.authToken || localStorage.getItem("keycloak_token") || localStorage.getItem("authToken");
    if (authToken) {
      headers.set("Authorization", `Bearer ${authToken}`);
    }
    return headers;
  }
});

export const backendApi = createApi({
  reducerPath: "backendApi",
  baseQuery: baseQuery,
  tagTypes: ["Services", "Logs", "Auth", "token"],
  endpoints: (build) => ({
    postSendNotification: build.mutation({
      query(body) {
        let jsonBody = decodeURIComponent(body);

        return {
          url: "middleware/teamsNotif",
          method: "POST",
          body: JSON.parse(jsonBody)
        };
      }
    }),

    createLog: build.mutation({
      query(body) {
        return {
          url: "middleware/log",
          method: "PUT",
          body
        };
      },
      invalidatesTags: [{ type: "Logs", id: "LIST" }]
    }),

    getGeneratedToken: build.query({
      query: () => "middleware/generatedToken",
      transformResponse: (response) => {
        if (response.token) {
          return response.token;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result, error, id) => [{ type: "token", id }]
    }),

    getAllServices: build.query({
      query: () => "middleware/services",
      transformResponse: (response) => {
        if (response.services.length > 0) {
          const newData = response.services.map((service) => ({
            ...service
          }));
          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Services", id })), { type: "Services", id: "LIST" }] : [{ type: "Services", id: "LIST" }])
    }),

    getAllInfoService: build.query({
      query: (duration) => `middleware/allInfoService/${duration}`,
      transformResponse: (response) => {
        if (response.infosService.length > 0) {
          const newData = response.infosService.map((service) => ({
            ...service
          }));

          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Services", id })), { type: "Services", id: "LIST" }] : [{ type: "Services", id: "LIST" }])
    }),

    getAllInfoByService: build.query({
      query: (param) => `middleware/allInfoByService/${param.service}/${param.duration}`,
      keepUnusedDataFor: timeInCacheShort
    }),

    getAllBreakingLog: build.query({
      query: (duration) => `middleware/allBreakingLog/${duration}`,
      transformResponse: (response) => {
        if (response.nbresultats > 0) {
          const newData = response.breakingLogs.map((log) => ({
            ...log
          }));

          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    createService: build.mutation({
      query(body) {
        return {
          url: "middleware/service",
          method: "PUT",
          body
        };
      },
      invalidatesTags: [{ type: "Services", id: "LIST" }]
    }),

    updateService: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `middleware/service/${id}`,
          method: "POST",
          body
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Services", id },
        { type: "Services", id: "LIST" }
      ]
    }),

    deleteService: build.mutation({
      query(id) {
        return {
          url: `middleware/service/${id}`,
          method: "DELETE"
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Users", id },
        { type: "Users", id: "LIST" }
      ]
    }),

    getServiceStatus: build.query({
      query: () => `lireServiceStatus`,
      transformResponse: (response) => {
        if (response.serviceStatus.length > 0) {
          return response.serviceStatus;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    /** - OK is used ------------------------------------------------------- */
    getServiceStatusOnDuration: build.query({
      query: (duration) => `middleware/serviceStatus/${duration}`,
      transformResponse: (response) => {
        if (response.serviceStatus.length > 0) {
          return response.serviceStatus;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),
    /** --------------------------------------------------------------------  */
    getLastDateErrors: build.query({
      query: () => `lireDerniereErreurParService`,
      transformResponse: (response) => {
        if (response.lastErrors.length > 0) {
          return response.lastErrors;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),

    /** - OK is used ------------------------------------------------------- */
    getLastLogsByService: build.query({
      query: (duration) => `middleware/lastLogByService/${duration}`,
      transformResponse: (response) => {
        if (response?.lastLogs != null) {
          return response.lastLogs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort
    }),
    /** --------------------------------------------------------------------  */
    getAllLogs: build.query({
      query: () => `lireLogs`,
      transformResponse: (response) => {
        if (response.logs.length > 0) {
          const newData = response.logs.map((log) => ({
            ...log,
            created_at: isoStrToDate(log.created_at)
          }));

          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    /** - OK is used ------------------------------------------------------- */
    getLogsCurrentDay: build.query({
      query: () => `middleware/logsCurrentDay`,
      transformResponse: (response) => {
        if (response.logs.length >= 0) {
          return response.logs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),
    /** --------------------------------------------------------------------  */

    getLogsBySelectedDay: build.query({
      query: (selectedDay) => `middleware/${selectedDay}`,
      transformResponse: (response) => {
        if (response.logs.length >= 0) {
          return response.logs;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    getLogsByService: build.query({
      query: (p) => `middleware/logsByService/${p.service}/${p.duration}/${p.criticite}`,
      keepUnusedDataFor: timeInCacheShort
    }),

    checkDoublon: build.query({
      query: (p) => `middleware/doublon/true`,
      keepUnusedDataFor: timeInCacheShort
    }),

    postGetLogsByParams: build.mutation({
      query(body) {
        let jsonBody = JSON.parse(decodeURIComponent(body));

        return {
          url: "middleware/lireLogsParCriteres",
          method: "POST",
          body: jsonBody
        };
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Logs", id })), { type: "Logs", id: "LIST" }] : [{ type: "Logs", id: "LIST" }])
    }),

    getOrders: build.query({
      query: () => "order",
      transformResponse: (response) => {
        if (response.order.length > 0) {
          const newData = response.order.map((order) => ({
            ...order,
            created_at: isoStringToDate(order.created_at)
          }));
          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Orders", id })), { type: "Orders", id: "LIST" }] : [{ type: "Orders", id: "LIST" }])
    }),
    getAllOrdersFromUser: build.query({
      query: (id) => `order/histo/${id}`,
      transformResponse: (response) => {
        if (response.order.length > 0) {
          const newData = response.order
            .map((order) => ({
              ...order,
              created_at: isoStringToDate(order.created_at)
            }))
            .reverse();
          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "Orders", id })), { type: "Orders", id: "LIST" }] : [{ type: "Orders", id: "LIST" }])
    }),
    getOrder: build.query({
      query: (id) => `order/${id}`,
      providesTags: (result, error, id) => [{ type: "Orders", id }]
    }),

    postOrder: build.mutation({
      query(body) {
        return {
          url: "order",
          method: "POST",
          body
        };
      },
      invalidatesTags: [{ type: "Orders", id: "LIST" }]
    }),

    updateOrder: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `order/${id}`,
          method: "PUT",
          body
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Orders", id },
        { type: "Orders", id: "LIST" }
      ]
    }),

    deleteOrder: build.mutation({
      query(id) {
        return {
          url: `order/${id}`,
          method: "DELETE"
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Orders", id },
        { type: "Orders", id: "LIST" }
      ]
    }),

    getOrdersWeb: build.query({
      query: () => "orderWeb",
      transformResponse: (response) => {
        if (response.orderWeb.length > 0) {
          const newData = response.orderWeb.map((orderWeb) => ({
            ...orderWeb,
            created_at: isoStrToDate(orderWeb.created_at),
            updated_at: isoStrToDate(orderWeb.updated_at)
          }));
          return newData;
        } else {
          return false;
        }
      },
      keepUnusedDataFor: timeInCacheShort,
      providesTags: (result) => (result ? [result.map(({ id }) => ({ type: "OrdersWeb", id })), { type: "OrdersWeb", id: "LIST" }] : [{ type: "OrdersWeb", id: "LIST" }])
    }),

    getOrderWeb: build.query({
      query: (id) => `orderWeb/${id}`,
      providesTags: (result, error, id) => [{ type: "OrdersWeb", id }]
    }),

    getOrderOrigin: build.query({
      query: (id) => `orderOrigin/${id}`,
      providesTags: (result, error, id) => [{ type: "OrderOrigin", id }]
    }),

    postOrderWeb: build.mutation({
      query(body) {
        return {
          url: "orderWeb",
          method: "POST",
          body
        };
      },
      invalidatesTags: [{ type: "OrdersWeb", id: "LIST" }]
    }),

    lockOrderWeb: build.mutation({
      query(data) {
        const { id } = data;
        return {
          url: `orderWeb/lock/${id}`,
          method: "POST"
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "OrdersWeb", id },
        { type: "OrdersWeb", id: "LIST" }
      ]
    }),

    unlockOrderWeb: build.mutation({
      query(data) {
        const { id } = data;
        return {
          url: `orderWeb/unlock/${id}`,
          method: "POST"
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "OrdersWeb", id },
        { type: "OrdersWeb", id: "LIST" }
      ]
    }),

    updateOrderWeb: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `orderWeb/${id}`,
          method: "PUT",
          body
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "OrdersWeb", id },
        { type: "OrdersWeb", id: "LIST" }
      ]
    }),

    deleteOrderWeb: build.mutation({
      query(id) {
        return {
          url: `orderWeb/${id}`,
          method: "DELETE"
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "OrdersWeb", id },
        { type: "OrdersWeb", id: "LIST" }
      ]
    }),

    updatePassword: build.mutation({
      query(body) {
        return {
          url: "update-password",
          method: "POST",
          body
        };
      }
    }),

    getDocumentX3: build.query({
      query: (id) => `order/documentX3/${id}`,
      providesTags: (result, error, id) => [{ type: "DocumentX3", id }]
    })
  })
});

export const {
  usePostSendNotificationMutation,
  useCreateLogMutation,
  useGetAllServicesQuery,
  useGetAllInfoServiceQuery,
  useGetAllInfoByServiceQuery,

  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetLastLogsByServiceQuery,
  useGetLogsCurrentDayQuery,
  useGetLogsBySelectedDayQuery,
  useGetLogsByServiceQuery,
  useCheckDoublonQuery,

  useGetGeneratedTokenQuery,

  usePostUserMutation,
  usePostUpdateUserMutation,
  useDeleteUserMutation,
  usePostOrderMutation,
  useGetOrderQuery,
  useGetOrdersQuery,
  useGetAllOrdersFromUserQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  usePostOrderWebMutation,
  useGetOrderWebQuery,
  useGetOrderOriginQuery,
  useGetOrdersWebQuery,
  useUpdateOrderWebMutation,
  useLockOrderWebMutation,
  useUnlockOrderWebMutation,
  useDeleteOrderWebMutation,
  useGetAllBreakingLogQuery,
  useUpdatePasswordMutation,
  useGetDocumentX3Query,
  usePostGetLogsByParamsMutation
} = backendApi;
